import Server from "../server";
import {AppFunction, HashUrl} from "../misc/types";
import * as utils from "../misc/utils";
import {Factory} from "../functions/factory";
import jQuery from "jquery";
import {Spinner} from "../views/spinner";
import Page from "../framework/page";
import WrbPage from "../views/wrb";
import {User} from "../bo/user";

console.log("Loading app.ts...");

export abstract class AbstractApp {

    private factory = this.createFactory();

    protected readonly server = Server.instance();

    /**
     * The current function.
     */
    protected fn?: AppFunction;

    /**
     * Page representing the app
     */
    protected readonly page: Page;

    /**
     * What we currently think the HashUrl should be. If the browser window thinks otherwise, then we
     * need to refresh the view (eg, for back button handling)
     */
    protected current: HashUrl = utils.windowHashUrl();

    private inHashChange = false;

    /**
     * Keep track of whether we are currently in the gotoUrl handler or not.
     * If we are, then update to the URL are done via replaceState instead of pushState
     */
    private inGotoUrl = false;

    /**
     * Keep track of function changes. This is a crude mechanism to prevent the "PopFn" notification
     * popping out of the entire app in case someone opens the browser directly into an "edit" page.
     */
    private appFunctionUrls : HashUrl[] = [];

    private user: User | undefined;

    constructor() {

        this.server.runtimeParams_.then((r) => {
            const test = r["wrb.client.test"];
            if (test) {
                jQuery("body").addClass("test-mode");
            }
        });

        const hashUrl = utils.windowHashUrl();
        this.page = this.createPage(hashUrl);

        // Add a spinner to the document
        new Spinner().attach(document);
        jQuery(window).on("popstate hashchange", (e) => this.hashchange(e));

        const remember_ = this.server.loginRemembered(hashUrl);
        remember_.then((user) => {
            this.setUser(user);
            this.gotoUrl(hashUrl);
        });

    }

    protected abstract createFactory() : Factory;

    boot() {
        const hashUrl = utils.windowHashUrl();
        this.gotoUrl(hashUrl);
    }

    public getUser() : User | undefined {
        return this.user;
    }

    public setUser(user: User | undefined) {
        this.user = user;
        if (user) {
            this.server.setUser(user);
        }
        this.factory = this.createFactory();
    }

    /**
     * Whenever the hash url changes, this function is called (by the popstate and hashchange listener)
     * If the new hash url is not in sync with the current function (ie, this.current),
     * the function corresponding to the new URL is installed
     */
    protected hashchange(e: any) {

        if (this.inGotoUrl) {
            return;
        }

        const save = this.inHashChange;

        try {

            this.inHashChange = true;

            const newUrl = utils.windowHashUrl();

            console.log(`hashChange from ${this.current} to ${newUrl}`);

            if (newUrl != this.current) {
                this.gotoUrl(newUrl);
            }

        } finally {

            this.inHashChange = save;

        }

    }

    pushFunction(fn: AppFunction) {
        const hashUrl = fn.url();
        utils.pushHash(hashUrl);
        this.setFunction(fn);
    }

    setFunction(fn: AppFunction) {
        if (this.fn == fn) {
            return;
        }
        this.setFunction0(fn);
    }

    popFn() {
        // This could be made more sophisticated, eg use internal state
        const stack = this.appFunctionUrls;
        if (stack.length > 1) {
            this.gotoUrl(stack[stack.length - 2]);
            return true;
        } else {
            return false;
        }
    }

    // -----------------------------------------

    private setFunction0(fn: AppFunction) {

        this.server.clearVolatileCache();

        this.fn = fn;
        fn.view().setParent(this.page);

        this.configurePage(fn);

        this.page.update();

        const hash = fn.url();

        this.current = hash;

        const stack = this.appFunctionUrls;
        const top = stack.length > 0 ? stack[stack.length - 1] : undefined;
        const top2 = stack.length > 1 ? stack[stack.length - 2] : undefined;

        if (hash != top) {
            if (hash == top2) {
                stack.pop();
            } else {
                stack.push(hash);
            }
        }

        if (this.inHashChange) {
            utils.replaceHash(hash);
        } else {
            utils.pushHash(hash);
        }

    }

    protected abstract createPage(hashUrl: string) : Page;

    protected abstract configurePage(fn: AppFunction): void;

    fromUrl(hashUrl: HashUrl) {
        return this.factory.fromUrl(hashUrl, this.user);
    }

    gotoUrl(hashUrl: HashUrl) {

        const wasInGotoUrl = this.inGotoUrl;
        try {
            this.inGotoUrl = true;

            this.current = hashUrl;

            if (! wasInGotoUrl && ! this.inHashChange) {

                // We want to actively create a new history entry
                utils.pushHash(hashUrl);

            } else {

                // We don't want to create a history entry
                utils.replaceHash(hashUrl);

            }

            const autoUpdatePending = jQuery("body").hasClass("update-pending-auto");
            if (autoUpdatePending) {
                window.location.reload();
                return;
            }

            const f = this.functionFromUrl(hashUrl, this.user);
            this.setFunction(f);

        } finally {
            this.inGotoUrl = wasInGotoUrl;
        }

    }

    render() {
        this.page.updatePage();
    }

    public functionFromUrl(hashUrl: HashUrl, user: User | undefined) {
        const f = this.factory.fromUrl(hashUrl, user);
        return f;
    }

}

export class App extends AbstractApp {

    constructor() {
        super();
    }

    protected configurePage(fn: AppFunction) {

        const p = this.page as WrbPage;

        p.setContent(fn.view());
        p.customTemplate = fn.pageTemplate();


    }

    protected createPage(hashUrl: string) : Page {
        const page = new WrbPage(this);
        return page;
    }

    protected createFactory() {
        return new Factory(this);
    }
}
